import { useEffect, useState, useMemo } from 'react';
import { Box, TablePagination, Typography } from '@mui/material';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import { useTranslation } from 'react-i18next';
import Loading from '../Loading';
import MainTable from './MainTable';
import { useRoles } from '../../hooks/useRoles';

type LegendItem = {
    name: string;
    color: string;
};

interface IDataTableProps {
    columnTitles: any[];
    columnWidths?: any[];
    noInfoImage?: string;
    filterLocal?: boolean;
    legendConfig?: LegendItem[];
    legendField?: string;
    legendResolver?: (item: any) => string;
    showLegendFooter?: boolean;
    showLegendForAllRoles?: boolean;
    [key: string]: any;
}

function DataTable({
    columnTitles,
    columnWidths,
    noInfoImage,
    filterLocal = false,
    legendConfig,
    legendField,
    legendResolver,
    showLegendFooter = true,
    showLegendForAllRoles = false,
    ...props
}: IDataTableProps) {
    const { t } = useTranslation('common');
    const { filters, setFilters, items, isLoading, noPaginated, noInfoTitle, noInfoSubtitle } =
        props;
    const [rowsPerPage, setRowsPerPage] = useState(filters?.take || 5);
    const [page, setPage] = useState(0);
    // Normalize data: array for local mode, paginated API for remote
    const rawItems = useMemo(() => {
        if (!items) return [];
        if (filterLocal) return Array.isArray(items) ? items : items?.data?.data || [];
        return items?.data?.data || [];
    }, [items, filterLocal]);

    const { isAdminBO, isViewerBO } = useRoles();

    const canSeeLegend = showLegendForAllRoles || isAdminBO || isViewerBO;

    useEffect(() => {
        if (
            !filterLocal &&
            typeof filters?.skip === 'number' &&
            typeof filters?.take === 'number'
        ) {
            const newPage = Math.floor(filters.skip / filters.take);
            if (newPage !== page) {
                setPage(newPage);
            }
        }
    }, [filters.skip, filters.take, filterLocal]);

    const totalCount = useMemo(() => {
        if (filterLocal) return rawItems?.length || 0;
        return props?.count || items?.data?.count || 0;
    }, [rawItems, filterLocal, props?.count, items]);

    useEffect(() => {
        if (!filterLocal && filters?.skip === 0) setPage(0);
    }, [filters, filterLocal]);

    const handleChangeRowsPerPage = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        if (!filterLocal) {
            setFilters((prevFilters: any) => ({ ...prevFilters, take: newRowsPerPage, skip: 0 }));
        }
    };

    const handleChangePage = (_: any, newPage: number) => {
        if (filterLocal) {
            setPage(newPage);
        } else {
            const skip = filters.skip;
            const take = filters.take;
            setFilters({
                ...filters,
                skip: newPage > page ? skip + take : skip - take
            });
            setPage(newPage);
        }
    };

    // Slice items locally if filterLocal
    const paginatedItems = useMemo(() => {
        if (filterLocal) {
            const start = page * rowsPerPage;
            return rawItems.slice(start, start + rowsPerPage);
        }
        return rawItems;
    }, [rawItems, page, rowsPerPage, filterLocal]);

    const processedColumnTitles = useMemo(() => {
        const headers = props.headers;
        if (!headers) return columnTitles;

        return columnTitles.map((col: any, index: number) => {
            const sortName = headers[index]?.onClickColumnTitleSortName;
            if (!sortName) return col;

            const [currentField, currentDir] = (filters?.ordenador || '').split(':');
            const isActiveColumn = currentField === sortName;
            const isAsc = isActiveColumn && currentDir === 'asc';
            const labelText = typeof col === 'string' ? col : col?.label;

            const sortIndicator = (
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        height: 20,
                        ml: 0.5,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0, 0, 0, 0.15)',
                        '& svg': { fontSize: '1rem' }
                    }}
                >
                    {!isActiveColumn ? (
                        <Typography component="span" sx={{ lineHeight: 1, fontSize: '0.8rem' }}>
                            -
                        </Typography>
                    ) : isAsc ? (
                        <ExpandMoreOutlinedIcon />
                    ) : (
                        <ExpandLessOutlinedIcon />
                    )}
                </Box>
            );

            return {
                ...(typeof col === 'object' ? col : {}),
                label: (
                    <Box
                        display="flex"
                        alignItems="center"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                            if (!isActiveColumn) {
                                setFilters((prev: any) => ({
                                    ...prev,
                                    ordenador: `${sortName}:asc`
                                }));
                            } else if (isAsc) {
                                setFilters((prev: any) => ({
                                    ...prev,
                                    ordenador: `${sortName}:desc`
                                }));
                            } else {
                                setFilters((prev: any) => {
                                    const { ordenador, ...rest } = prev;
                                    return rest;
                                });
                            }
                        }}
                    >
                        <Typography
                            variant="body1"
                            fontWeight="bold"
                            sx={{ fontSize: '1rem', px: 0.5 }}
                        >
                            {labelText}
                        </Typography>
                        {sortIndicator}
                    </Box>
                )
            };
        });
    }, [columnTitles, props.headers, filters?.ordenador, setFilters]);

    if (isLoading) return <Loading />;
    return (
        <MainTable
            {...props}
            items={paginatedItems}
            columnTitles={processedColumnTitles}
            columnWidths={columnWidths}
            noInfoTitle={noInfoTitle}
            noInfoSubtitle={noInfoSubtitle}
            noInfoImage={noInfoImage}
            legendConfig={canSeeLegend ? legendConfig : undefined}
            legendField={canSeeLegend ? legendField : undefined}
            legendResolver={canSeeLegend ? legendResolver : undefined}
        >
            {showLegendFooter && legendConfig && legendConfig.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 3,
                        padding: '12px 16px',
                        alignItems: 'center'
                    }}
                >
                    {canSeeLegend &&
                        legendConfig?.map((item: any) => (
                            <Box
                                key={item.name}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: item.color
                                    }}
                                />
                                <span>{item.name}</span>
                            </Box>
                        ))}
                </Box>
            )}
            {!noPaginated && totalCount > 0 && (
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100, rowsPerPage].filter(
                        (value, index, self) => self.indexOf(value) === index
                    )}
                    labelDisplayedRows={({ from, to, count }) => {
                        return `${from}–${to} ${t('table.pagination.of')} ${count} items`;
                    }}
                    component="div"
                    rowsPerPage={rowsPerPage}
                    count={totalCount}
                    page={totalCount <= rowsPerPage ? 0 : page}
                    onPageChange={handleChangePage}
                    labelRowsPerPage={t('table.pagination.rowsPerPage')}
                    onRowsPerPageChange={(event) =>
                        handleChangeRowsPerPage(parseInt(event.target.value, 10))
                    }
                />
            )}
        </MainTable>
    );
}

export default DataTable;
